import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText, InputLabel, makeStyles, MenuItem,
  Modal, Select,
  TextareaAutosize,
  TextField
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { CancelRounded, Save, SaveRounded } from "@material-ui/icons";
import FormControl from "@material-ui/core/FormControl";
import { DateTime } from "luxon";
import { appointmentTimes } from "../../utilities/inspections";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1),
  },
}));

function TheDialog({ open, editLabel, saveHandler, closeHandler, inspection, children }) {
  return (
    <Dialog open={open !== null} onClose={closeHandler} maxWidth='sm' fullWidth={true}>

      <DialogContent>
        <div className={"tw-mb-4"}>
          <p className={"tw-text-black tw-font-bold tw-text-lg"}>{editLabel}</p>
          <p className={"tw-text-gray-700"}>{inspection.PolicyNo} - {inspection.Name}</p>
        </div>
       
        {children}

        <DialogActions className='sm:tw-mt-4'>
          <Button variant={"contained"} onClick={saveHandler} color={"primary"}
            startIcon={<SaveRounded/>}>
                        Save
          </Button>
          <Button variant={"contained"} onClick={closeHandler} color={"secondary"}
            startIcon={<CancelRounded/>}>
                        Cancel
          </Button>
        </DialogActions>

      </DialogContent>


    </Dialog>
  );
}


export default function InspectionPreEditModal({ open, closeHandler, editingType, saveHandler, inspection, folders = [] }) {

  const classes = useStyles();

  const [genericItem, setGenericItem] = useState("");
  const [address, setAddress] = useState({});
  const [gated, setGated] = useState("");
  const [appointment, setAppointment] = useState({});
  const [editLabel, setEditLabel] = useState("");
  const [timerTargetTime, setTimerTargetTime] = useState("");
  const [timerPreset, setTimerPreset] = useState("1day");

  const formatAppointmentDate = date => {
    const jsDate = new Date(date);
    return jsDate.toISOString();
  };

  const saveClicked = () => {
    switch (editingType) {
    case "notes":
      inspection.Notes = genericItem;
      saveHandler({ notes: genericItem });
      break;
    case "phone":
      inspection.Telephone = genericItem;
      saveHandler({ phone: genericItem });
      break;
    case "email":
      inspection.Email = genericItem;
      saveHandler({ email: genericItem });
      break;
    case "address":
      inspection.Address = address.address;
      inspection.City = address.city;
      inspection.County = address.county;
      inspection.State = address.state;
      inspection.Zipcode = address.zipcode;
      saveHandler({
        address: address.address,
        city: address.city,
        county: address.county,
        state: address.state,
        zipcode: address.zipcode
      });
      break;
    case "appointment":
      if (appointment.date === 0) {
        return;
      }
      inspection.AppointmentDate = appointment.date;
      inspection.AppointmentTime = appointment.time;
      saveHandler({ appointmentDate: formatAppointmentDate(appointment.date), appointmentTime: appointment.time });
      break;
    case "gated":
      inspection.Gated = gated;
      saveHandler({ gated: gated });
      break;
    case "gatecode":
      inspection.GateCode = genericItem;
      saveHandler({ gatecode: genericItem });
      break;
    case "folder": {
      inspection.FolderId = parseInt(genericItem);
      const selectedFolder = folders.find(f => f.id === parseInt(genericItem));
      const folderData = { folderId: parseInt(genericItem) };
      
      // If moving to individual timer folder, calculate and include timer
      if (selectedFolder && selectedFolder.timer_mode === 'individual') {
        let targetTime;
        
        if (timerPreset === 'custom') {
          if (!timerTargetTime) {
            // Show error if custom is selected but no time is set
            alert('Please set a custom date and time');
            return;
          }
          targetTime = timerTargetTime;
        } else {
          // Calculate target time based on preset
          const now = new Date();
          const presetHours = {
            '1day': 24,
            '2days': 48, 
            '3days': 72,
            '1week': 168,
            '2weeks': 336
          };
          
          now.setHours(now.getHours() + presetHours[timerPreset]);
          targetTime = now.toISOString().slice(0, 16); // Format for datetime-local
        }
        
        folderData.timerTargetTime = targetTime;
      }
      
      saveHandler(folderData);
      break;
    }
    }
  };

  const initFields = () => {
    switch (editingType) {
    case "notes":
      setGenericItem(inspection.Notes);
      setEditLabel("Private Notes");
      break;
    case "phone":
      setGenericItem(inspection.Telephone);
      setEditLabel("Telephone");
      break;
    case "email":
      setGenericItem(inspection.Email);
      setEditLabel("Email");
      break;
    case "address":
      setEditLabel("Address");
      setAddress({
        address: inspection.Address,
        city: inspection.City,
        county: inspection.County,
        state: inspection.State,
        zipcode: inspection.Zipcode
      });
      break;
    case "appointment":
      setEditLabel("Appointment");
      setAppointment({ date: 0, time: 0 });
      break;
    case "gated":
      setGated(inspection.Gated);
      setEditLabel("Gated Community");
      break;
    case "gatecode":
      setGenericItem(inspection.GateCode);
      setEditLabel("Gate Code");
      break;
    case "folder":
      setGenericItem(inspection.FolderId || 0);
      setTimerTargetTime("");
      setTimerPreset("1day");
      setEditLabel("Folder");
      break;
    }
  };

  useEffect(() => {
    initFields();
  }, [editingType, inspection]);

  const getEditForm = () => {
    switch (editingType) {
    case "address":
      return (
        <>
          <TextField className={classes.root} fullWidth label="Address" value={address.address} onChange={(e) => setAddress(prevState => ({
            ...prevState,
            address: e.target.value
          }))}/>
          <TextField className={classes.root} fullWidth label="City" value={address.city} onChange={(e) => setAddress(prevState => ({
            ...prevState,
            city: e.target.value
          }))}/>
          <TextField className={classes.root} fullWidth label="County" value={address.county} onChange={(e) => setAddress(prevState => ({
            ...prevState,
            county: e.target.value
          }))}/>
          <TextField className={classes.root} label="State" value={address.state} onChange={(e) => setAddress(prevState => ({
            ...prevState,
            state: e.target.value
          }))}/>
          <TextField className={classes.root} label="Zip Code" value={address.zipcode} onChange={(e) => setAddress(prevState => ({
            ...prevState,
            zipcode: e.target.value
          }))}/>
        </>
      );
    case "appointment":
      return (
        <>
          <p>Appointment Date</p>
          <TextField fullWidth className={classes.root} type="date" value={appointment.date} onChange={(e) => setAppointment(prevState => ({
            ...prevState,
            date: e.target.value
          }))}/>
          <p className="tw-mt-4">Appointment Time</p>
          <FormControl fullWidth className={classes.root}>
            <Select
              value={appointment.time}
              onChange={(e) => setAppointment(prevState => ({
                ...prevState,
                time: e.target.value
              }))}
            >
              <MenuItem value={0}>--</MenuItem>
              {appointmentTimes().map((time, index) =>
                <MenuItem key={index} value={time.id}>{time.name}</MenuItem>
              )}
            </Select>
          </FormControl>
        </>
      );
    case "notes":
      return (
        <TextareaAutosize autoFocus className="tw-w-full" rowsMin={6} placeholder="" value={genericItem}
          onChange={(e) => setGenericItem(e.target.value)}/>
      );
    case "gated":
      return (
        <FormControl fullWidth className={classes.root}>
          <Select
            value={gated}
            onChange={(e) => setGated(e.target.value)}
          >
            <MenuItem value={"yes"}>Yes</MenuItem>
            <MenuItem value={"no"}>No</MenuItem>
          </Select>
        </FormControl>
      );
    case "folder": {
      const selectedFolder = folders.find(f => f.id === parseInt(genericItem));
      const isIndividualTimerFolder = selectedFolder && selectedFolder.timer_mode === 'individual';
      
      return (
        <>
          <FormControl fullWidth className={classes.root}>
            <Select
              value={genericItem}
              onChange={(e) => setGenericItem(e.target.value)}
            >
              <MenuItem value={0}>No Folder</MenuItem>
              {folders.map(folder => 
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                  {folder.timer_mode === 'individual' && <span className="tw-text-green-600 tw-ml-2">(Individual Timer)</span>}
                  {folder.timer_mode === 'fixed' && <span className="tw-text-blue-600 tw-ml-2">(Fixed: {folder.timer_hours}h)</span>}
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          {isIndividualTimerFolder && 
            <div className="tw-mt-4">
              <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                Set timer for this inspection (when to move to next folder)
              </p>
              
              <FormControl fullWidth className={classes.root} style={{ marginBottom: '12px' }}>
                <Select
                  value={timerPreset}
                  onChange={(e) => setTimerPreset(e.target.value)}
                >
                  <MenuItem value="1day">1 Day</MenuItem>
                  <MenuItem value="2days">2 Days</MenuItem>
                  <MenuItem value="3days">3 Days</MenuItem>
                  <MenuItem value="1week">1 Week</MenuItem>
                  <MenuItem value="2weeks">2 Weeks</MenuItem>
                  <MenuItem value="custom">Custom Date & Time</MenuItem>
                </Select>
              </FormControl>
              
              {timerPreset === 'custom' && 
                <TextField
                  fullWidth
                  type="datetime-local"
                  className={classes.root}
                  value={timerTargetTime}
                  onChange={(e) => setTimerTargetTime(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16), // Don't allow past times
                  }}
                />
              }
            </div>
          }
        </>
      );
    }
    default:
      return (
        <TextField className={classes.root} fullWidth label="" value={genericItem} onChange={(e) => setGenericItem(e.target.value)}/>
      );

    }
  };


  return (
    <>
      <TheDialog open={editingType} editLabel={editLabel} closeHandler={closeHandler} saveHandler={saveClicked}
        inspection={inspection}>
        {getEditForm()}
      </TheDialog>
    </>
  );
}
